import { canPurgeData } from '@/auth/roles';
import { AlertBanner } from '@/components/alert-banner';
import { InfoCard } from '@/components/info-card';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getOrg, purgeDistrictDataDryRun, purgeDistrictDataEnrolledStudents } from '@/lib/api-calls';
import { getEnvironmentLevel } from '@/lib/common-utils';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { PurgeDryRunTable } from '@airreading/common/data';
import { PurgeConfirmationGate } from '../-components/purge-confirmation-gate';
import { PurgeDryRunDownloads } from '../-components/purge-dry-run-download';
import { PurgeEnrolledStudentsGate } from '../-components/purge-enrolled-students-gate';
import { districtDetailsTabs } from '../-components/tabs';

export const Route = createFileRoute('/_layout/orgs/$orgId/settings')({
  beforeLoad: validateRouteRole(canPurgeData, { allowAnyAirAdmin: false }),
  component: DistrictSettingsPage,
});

function DistrictSettingsPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load district. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <DistrictSettings />
    </InfoPageSuspenseBoundary>
  );
}

function DistrictSettings() {
  const { orgId } = Route.useParams();
  const { data } = useSuspenseQuery(getOrg(orgId));

  return (
    <InfoPage
      headerText={data.name}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Districts',
            link: {
              to: '/orgs',
            },
          },
        ],
        currentPage: data.name,
      }}
      tabs={districtDetailsTabs({ orgId: data.id }, 'settings')}
    >
      <DeleteAllDataCard districtId={data.id} districtName={data.name} />
    </InfoPage>
  );
}

function DeleteAllDataCard({ districtId, districtName }: { districtId: string; districtName: string }) {
  const { toast } = useToast();

  const isProduction = getEnvironmentLevel() === 'prod';

  const [confirmedName, setConfirmedName] = useState<string | null>(null);

  const onConfirmed = useCallback(
    (name: string) => {
      setConfirmedName(name);
      toast({ title: `Checking student statuses for ${districtName}` });
    },
    [districtName, toast],
  );

  const runDryRun = useCallback(
    (table: PurgeDryRunTable, part: number) =>
      purgeDistrictDataDryRun({ districtId, table, part, data: { districtName: confirmedName ?? districtName } }),
    [districtId, confirmedName, districtName],
  );

  const onEnrolledStudentsCleared = useCallback(
    () => toast({ title: `Data purge dry run started for ${districtName}` }),
    [districtName, toast],
  );

  const checkEnrolledStudents = useCallback(
    () => purgeDistrictDataEnrolledStudents({ districtId, data: { districtName: confirmedName ?? districtName } }),
    [districtId, confirmedName, districtName],
  );

  return (
    <InfoCard headerText="Delete all data" maxItemsPerRow={1}>
      {isProduction ? (
        <AlertBanner
          title="Not available on production"
          content="Deleting all data cannot be run from the production portal."
        />
      ) : (
        <AlertBanner
          title="Feature in progress"
          content="This feature is in development. Data is not deleted, or queued to be deleted. This feature is not available on production."
        />
      )}
      <PurgeConfirmationGate
        expectedName={districtName}
        label="Confirm District Name"
        action="Confirm"
        note="Before deleting data we will generate CSV exports of the data that would be deleted. Proceed to generate exports. After validating exports we will ask you to confirm again before deleting data."
        disabled={isProduction}
        testId="district-settings-delete-all-data"
        onConfirmed={onConfirmed}
      />
      {confirmedName !== null && (
        <PurgeEnrolledStudentsGate checkEnrolledStudents={checkEnrolledStudents} onCleared={onEnrolledStudentsCleared}>
          <PurgeDryRunDownloads runDryRun={runDryRun} />
        </PurgeEnrolledStudentsGate>
      )}
    </InfoCard>
  );
}
